import React, { useState, useMemo } from "react";
import { CustomCheckoutModal } from "../modals/CustomCheckoutModal";
import { CancelSubscriptionModal } from "../modals/CancelSubscriptionModal";
import { CryptoCheckoutModal } from "../modals/CryptoCheckoutModal";
import { UpgradeConfirmationModal } from "../modals/UpgradeConfirmationModal";
import { PlanCard } from "../components/PlanCard";
import { FeatureComparisonTable } from "../components/FeatureComparisonTable";
import { CreditCalculator } from "../components/CreditCalculator";
import { Testimonial } from "../components/Testimonial";
import Button from "../../components/Button";
import { track } from "@crm/analytics";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export function PlansTab({
    plans,
    currentPlanDetails,
    currentPlanId,
    currentSubscription,
    isInTrial,
    isYearly,
    setIsYearly,
    selectedPlan,
    setSelectedPlan,
    showEmbeddedCheckout,
    setShowEmbeddedCheckout,
    isLoading,
    checkoutLoading,
    updateLoading,
    resumeLoading,
    cancelLoading,
    user,
    paymentMethods,
    addPaymentMethod,
    updateSubscription,
    resumeSubscription,
    createSubscriptionCheckout,
    onCancelSubscription,
    onPauseSubscription,
    onContactSupport,
    handleBillingPortal,
    calculateSavings,
    getPlanComparison,
    getProductId,
    planHoverCount,
    setPlanHoverCount,
    updateError,
    isPaymentUpdateError,
    pendingInvoiceUrl,
    hasActiveSubscription,
    onNavigateToCalculator,
    referralCode,
    t,
}) {
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showCryptoCheckout, setShowCryptoCheckout] = useState(false);
    const [cryptoSelectedPlan, setCryptoSelectedPlan] = useState(null);
    const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
    const [pendingUpgradePlan, setPendingUpgradePlan] = useState(null);

    // Get the 3 main plans (Good / Better / Best)
    const mainPlans = useMemo(() => {
        const filtered = plans.filter(
            (plan) =>
                !plan.hidden &&
                plan.name !== "Build with You" &&
                plan.name !== "Enterprise Plus"
        );
        // Take first 3 plans and assign tiers
        return filtered.slice(0, 3).map((plan, index) => ({
            ...plan,
            tier: index === 0 ? "good" : index === 1 ? "better" : "best",
            // Add key differentiators (3-4 per plan)
            keyFeatures: getKeyFeatures(plan),
        }));
    }, [plans]);

    // Extract 3-4 key differentiators per plan (using translation keys)
    function getKeyFeatures(plan) {
        const keyDifferentiators = {
            "Minimum Package": [
                t("billing.planFeatures.monthlyCredits200"),
                t("billing.planFeatures.internalUsers5"),
                t("billing.planFeatures.standardIntegrations"),
                t("billing.planFeatures.customDomain"),
            ],
            "Innovator Package": [
                t("billing.planFeatures.monthlyCredits400"),
                t("billing.planFeatures.internalUsers10"),
                t("billing.planFeatures.sqlIntegration"),
                t("billing.planFeatures.internalCompanyPortal"),
            ],
            "Visionary Package": [
                t("billing.planFeatures.monthlyCredits1000"),
                t("billing.planFeatures.internalUsers25"),
                t("billing.planFeatures.unlimitedEnvironments"),
                t("billing.planFeatures.premiumSupport"),
            ],
        };
        return keyDifferentiators[plan.name] || plan.features.slice(0, 4);
    }

    // Detect if current subscription is on a yearly billing cycle
    // Only block yearly→monthly when subscription is actually active.
    // past_due / incomplete / unpaid / canceled / incomplete_expired all
    // need the toggle UNLOCKED so users can recover by switching plans —
    // the backend (updateSubscription.js) already routes those statuses
    // through the create/replace path and skips the yearly→monthly check.
    const hasUsableSubscription = currentSubscription &&
        currentSubscription.status?.toLowerCase() === "active" &&
        !currentSubscription.cancelAtPeriodEnd;
    const isCurrentYearly = hasUsableSubscription && currentPlanDetails && (
        currentPlanId === currentPlanDetails.priceIdYearly ||
        currentPlanId === currentPlanDetails.devPriceIdYearly
    );

    const handlePlanSelection = (plan) => {
        track("plan_card_clicked", { plan: plan.name });

        if (plan.price === "Contact us") {
            window.location.href = "https://www.agentui.ai/contact/";
            return;
        }

        // Block yearly → monthly switches (credits cannot be refunded)
        if (isCurrentYearly && !isYearly) {
            toast.error(
                t("billing.yearlyToMonthlyBlocked") ||
                "You cannot switch from a yearly plan to a monthly plan. Your yearly subscription includes pre-allocated credits. Please wait until your billing period ends or contact support."
            );
            return;
        }

        setSelectedPlan(plan);

        // Only do an in-place switch when the current subscription is truly
        // active. past_due / incomplete / unpaid all mean the existing default
        // payment method has already failed — calling updateSubscription
        // without collecting a fresh card just creates another incomplete sub
        // with the same dead card. Route those users through the embedded
        // checkout so they enter a working card before we hit Stripe.
        const isUpdateable =
            hasUsableSubscription && currentPlanDetails && !isInTrial;

        if (isUpdateable) {
            setPendingUpgradePlan(plan);
            setShowUpgradeConfirm(true);
        } else {
            track("checkout_started", { plan: plan.name, mode: "custom" });
            setShowEmbeddedCheckout(true);
        }
    };

    const handleConfirmUpgrade = async () => {
        if (!pendingUpgradePlan) return;
        const productIdToUse = getProductId(pendingUpgradePlan);
        setShowUpgradeConfirm(false);
        await handleSubscriptionUpdate(productIdToUse, isYearly, pendingUpgradePlan);
        setPendingUpgradePlan(null);
    };

    const handleSubscriptionUpdate = async (productId, yearly = isYearly, plan) => {
        try {
            if (
                currentSubscription &&
                (currentSubscription.status?.toLowerCase() === "paused" ||
                    currentSubscription.cancelAtPeriodEnd)
            ) {
                const resumeResponse = await resumeSubscription({ userId: user?.id });
                if (resumeResponse?.status !== 200) {
                    console.error("Failed to auto-resume:", resumeResponse?.error);
                }
            }

            const response = await updateSubscription({
                userId: user?.id,
                productId,
                isYearly: yearly,
            });

            if (response?.status === 200) {
                window.location.reload();
            } else if (response?.status === 404) {
                track("checkout_started_for_new_subscription", { plan: plan?.name, isYearly: yearly });
                const checkoutResponse = await createSubscriptionCheckout({
                    productId,
                    isYearly: yearly,
                    successUrl: `${window.location.origin}/billing?success=true`,
                    cancelUrl: `${window.location.origin}/billing?canceled=true`,
                    referral: referralCode,
                });
                if (checkoutResponse?.data?.url) {
                    window.location.href = checkoutResponse.data.url;
                }
            }
        } catch (error) {
            console.error("Error handling subscription:", error);
            toast.error("Failed to process subscription. Please try again.");
        }
    };

    const handleResume = async () => {
        const response = await resumeSubscription({ userId: user?.id });
        if (response?.status === 200) {
            window.location.reload();
        }
    };

    // Check if this is the current plan tier (any billing cycle)
    const isCurrentPlanTier = (plan) => {
        return (
            (plan.priceIdMonthly === currentPlanId && plan.priceIdMonthly) ||
            (plan.priceIdYearly === currentPlanId && plan.priceIdYearly) ||
            (plan.devPriceIdMonthly === currentPlanId && plan.devPriceIdMonthly) ||
            (plan.devPriceIdYearly === currentPlanId && plan.devPriceIdYearly) ||
            plan.productId === currentPlanDetails?.productId
        );
    };

    // Check if this is the EXACT current plan (same billing cycle)
    const isCurrentPlan = (plan) => {
        const selectedPriceId = isYearly
            ? plan.devPriceIdYearly || plan.priceIdYearly
            : plan.devPriceIdMonthly || plan.priceIdMonthly;
        return selectedPriceId === currentPlanId && !isInTrial;
    };

    // Check if this is a billing cycle change (same tier, different cycle)
    // Only treat as a true in-place cycle change when the sub is actually
    // usable. For past_due / incomplete / unpaid the click goes through the
    // embedded checkout, so the button should read "Get Started", not
    // "Switch to Monthly", and the green "Switch to … billing" callout
    // should stay hidden.
    const isBillingCycleChange = (plan) => {
        return hasUsableSubscription && isCurrentPlanTier(plan) && !isCurrentPlan(plan);
    };

    const needsResume = (plan) => {
        return (
            isCurrentPlan(plan) &&
            (currentSubscription?.status?.toLowerCase() === "paused" ||
                currentSubscription?.cancelAtPeriodEnd)
        );
    };

    const isPaused = currentSubscription?.status?.toLowerCase() === "paused";
    const isCanceling = currentSubscription?.cancelAtPeriodEnd;

    return (
        <>
            {/* Modals */}
            <CustomCheckoutModal
                isOpen={showEmbeddedCheckout}
                onClose={() => setShowEmbeddedCheckout(false)}
                selectedPlan={selectedPlan}
                isYearly={isYearly}
                userId={user?.id}
                currentUser={user}
                addPaymentMethod={addPaymentMethod}
                updateSubscription={updateSubscription}
                paymentMethods={paymentMethods}
                t={t}
            />
            <CryptoCheckoutModal
                isOpen={showCryptoCheckout}
                onClose={() => {
                    setShowCryptoCheckout(false);
                    setCryptoSelectedPlan(null);
                }}
                selectedPlan={cryptoSelectedPlan}
                isYearly={isYearly}
                userId={user?.id}
                t={t}
            />
            <CancelSubscriptionModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onCancel={onCancelSubscription}
                onPause={onPauseSubscription}
                onContactSupport={onContactSupport}
                currentPlan={currentPlanDetails?.name || "Current Plan"}
                t={t}
            />
            <UpgradeConfirmationModal
                isOpen={showUpgradeConfirm}
                onClose={() => {
                    setShowUpgradeConfirm(false);
                    setPendingUpgradePlan(null);
                }}
                onConfirm={handleConfirmUpgrade}
                selectedPlan={pendingUpgradePlan}
                currentPlanDetails={currentPlanDetails}
                isYearly={isYearly}
                isUpgrade={pendingUpgradePlan ? getPlanComparison(pendingUpgradePlan) === "upgrade" : false}
                isDowngrade={pendingUpgradePlan ? getPlanComparison(pendingUpgradePlan) === "downgrade" : false}
                isBillingCycleChange={pendingUpgradePlan ? isBillingCycleChange(pendingUpgradePlan) : false}
                isLoading={updateLoading}
                t={t}
            />

            {/* Main Content */}
            <div data-plans-section className="space-y-12">
                {/* Header */}
                <div className="text-center max-w-2xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                        {t("billing.choosePlanTitle") || "Choose your plan"}
                    </h2>
                    <p className="text-lg text-slate-500">
                        {t("billing.planSubtitle")}
                    </p>
                </div>

                {/* Billing Toggle */}
                <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center justify-center gap-4">
                        <span
                            className={`text-sm font-medium transition-colors ${
                                !isYearly ? "text-slate-900" : "text-slate-400"
                            }`}
                        >
                            {t("billing.monthly")}
                        </span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={isYearly}
                            onClick={() => {
                                if (isCurrentYearly && isYearly) {
                                    toast.error(
                                        t("billing.yearlyToMonthlyBlocked") ||
                                        "You cannot switch from a yearly plan to a monthly plan. Your yearly subscription includes pre-allocated credits. Please wait until your billing period ends or contact support."
                                    );
                                    return;
                                }
                                const newValue = !isYearly;
                                setIsYearly(newValue);
                                track("pricing_toggle_changed", { value: newValue ? "annual" : "monthly" });
                            }}
                            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                isYearly ? "bg-indigo-600" : "bg-slate-200"
                            }`}
                        >
                            <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                                    isYearly ? "translate-x-8" : "translate-x-1"
                                }`}
                            />
                        </button>
                        <div className="flex items-center gap-2">
                            <span
                                className={`text-sm font-medium transition-colors ${
                                    isYearly ? "text-slate-900" : "text-slate-400"
                                }`}
                            >
                                {t("billing.yearly")}
                            </span>
                            <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full">
                                {t("billing.savePercent", { percent: "20%" }) || "Save 20%"}
                            </span>
                        </div>
                    </div>
                    {isCurrentYearly ? (
                        <p className="text-xs text-amber-600">
                            {t("billing.yearlyLocked") || "You are on a yearly plan. Switching to monthly is not available until your billing period ends."}
                        </p>
                    ) : (
                        <p className="text-xs text-slate-500">
                            {isYearly
                                ? (t("billing.billedYearlyNote") || "Prices shown per month, billed annually (full year charged upfront)")
                                : (t("billing.billedMonthlyNote") || "Prices shown per month, billed monthly")}
                        </p>
                    )}
                </div>

                {/* Plan Cards - Good / Better / Best */}
                <div className="pt-6">
                <div className="grid md:grid-cols-3 gap-6 md:gap-4 items-stretch max-w-5xl mx-auto">
                    {mainPlans.map((plan) => {
                        const planComparison = getPlanComparison(plan);
                        const planIsBillingCycleChange = isBillingCycleChange(plan);
                        const isUpgrade = planComparison === "upgrade" || planIsBillingCycleChange;
                        const isDowngrade = planComparison === "downgrade";

                        return (
                            <PlanCard
                                key={plan.name}
                                plan={{ ...plan, savings: calculateSavings(plan) }}
                                tier={plan.tier}
                                isCurrentPlan={isCurrentPlan(plan)}
                                isRecommended={plan.recommended}
                                isLoading={checkoutLoading || isLoading || updateLoading || resumeLoading}
                                needsResume={needsResume(plan)}
                                isPaused={isCurrentPlan(plan) && isPaused}
                                isCanceling={isCurrentPlan(plan) && isCanceling}
                                isUpgrade={isUpgrade}
                                isDowngrade={isDowngrade}
                                isBillingCycleChange={planIsBillingCycleChange}
                                hasActiveSubscription={hasActiveSubscription && hasUsableSubscription}
                                onSelect={handlePlanSelection}
                                onResume={handleResume}
                                isYearly={isYearly}
                                t={t}
                            />
                        );
                    })}
                </div>
                </div>

                {/* Credit Calculator + Testimonial Row */}
                <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                    <CreditCalculator t={t} onNavigateToCalculator={onNavigateToCalculator} />
                    <Testimonial t={t} />
                </div>

                {/* Compare All Features */}
                <div className="max-w-5xl mx-auto">
                    <FeatureComparisonTable plans={plans} t={t} />
                </div>

                {/* Enterprise CTA - Contact Us Link */}
                <div className="text-center pt-8 border-t border-slate-100 max-w-2xl mx-auto">
                    <p className="text-slate-600 mb-4">
                        {t("billing.needMore") || "Need more? We have enterprise solutions."}
                    </p>
                    <a
                        href="https://calendar.app.google/r1NEgK7oWPMoUxur9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold group"
                    >
                        {t("billing.contactUs") || "Contact us for Enterprise"}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>

                {/* Cancel Subscription */}
                {currentSubscription &&
                    !isInTrial &&
                    ["active", "past_due"].includes(currentSubscription.status?.toLowerCase()) &&
                    !currentSubscription.cancelAtPeriodEnd && (
                        <div className="text-center pt-8">
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className="text-slate-400 hover:text-red-500 text-sm font-medium transition-colors"
                            >
                                {t("billing.cancelSubscription") || "Cancel Subscription"}
                            </button>
                        </div>
                    )}

                {/* Resume for Paused/Canceled */}
                {currentSubscription &&
                    !isInTrial &&
                    (currentSubscription.status?.toLowerCase() === "paused" ||
                        currentSubscription.cancelAtPeriodEnd) && (
                        <div className="text-center space-y-4 pt-8">
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-md mx-auto">
                                <p className="text-amber-800 text-sm">
                                    {currentSubscription.cancelAtPeriodEnd
                                        ? t("billing.subscriptionScheduledForCancellation") ||
                                          "Your subscription is scheduled to be canceled."
                                        : t("billing.subscriptionPaused") ||
                                          "Your subscription is currently paused."}
                                </p>
                            </div>
                            <Button
                                onClick={handleResume}
                                disabled={resumeLoading}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-8 rounded-xl"
                            >
                                {resumeLoading
                                    ? t("billing.resuming") || "Resuming..."
                                    : t("billing.resumeSubscription") || "Resume Subscription"}
                            </Button>
                        </div>
                    )}
            </div>
        </>
    );
}
